import type { TRPC } from '../contexts/TRPC';
import { isLeftResult } from '../lib/trpcTypeGuards';
import { calculateFileHash } from './crypto';
import { getImageOnlyDimensions, getMediaDimensions } from './imageDimensions';

type TRPCClient = TRPC['client'];

type PortalUploadFolder =
  | 'avatars'
  | 'logos'
  | 'banners'
  | 'previews'
  | 'documents';

type PortalUploadFileType =
  | 'AppIcon'
  | 'ReleaseBanner'
  | 'ReleaseFeatureGraphic'
  | 'ReleaseScreenshot';

type UploadMode = 'image' | 'media';

interface UploadPortalMediaFileInput {
  trpc: TRPCClient;
  file: File;
  folder: PortalUploadFolder;
  fileType: PortalUploadFileType;
  mode: UploadMode;
}

export async function uploadPortalMediaFile({
  trpc,
  file,
  folder,
  fileType,
  mode,
}: UploadPortalMediaFileInput): Promise<string> {
  const fileExtension = file.name.split('.').pop() || 'jpg';
  const mimeType = file.type || 'application/octet-stream';
  const fileHash = await calculateFileHash(file);

  let dimensions: { width?: number; height?: number } | undefined;
  try {
    dimensions =
      mode === 'image'
        ? await getImageOnlyDimensions(file)
        : await getMediaDimensions(file, mimeType);
  } catch (error) {
    console.warn('Failed to derive media dimensions for upload record:', error);
  }

  const uploadResult = await trpc.generateUploadUrl.mutate({
    fileExtension,
    fileType: mimeType,
    folder,
  });

  if (isLeftResult(uploadResult)) {
    throw new Error(uploadResult.left.message);
  }

  const { key, publicUrl, uploadUrl } = uploadResult.right;

  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': mimeType,
    },
  });

  if (!response.ok) {
    const responseText = await response.text();
    throw new Error(
      `Failed to upload file: ${response.status} ${response.statusText} - ${responseText}`,
    );
  }

  try {
    const recordResult = await trpc.arweave.recordUpload.mutate({
      fileHash,
      fileType,
      fileSize: file.size,
      mimeType,
      provider: 'S3',
      facilitator: 'S3',
      providerId: key,
      providerUri: publicUrl,
      metadata: dimensions,
      purpose: 'DevPortalDisplay',
    });

    if (isLeftResult(recordResult)) {
      console.warn(
        'Failed to record portal media upload metadata:',
        recordResult.left.message,
      );
    }
  } catch (error) {
    console.warn('Error recording portal media upload metadata:', error);
  }

  return publicUrl;
}
